import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { usePortal } from '../context/PortalContext';
import { clubService } from '../services/clubService';
import {
  Club,
  ClubPaymentAccount,
  ClubPaymentAccountMode,
  ClubPaymentAccountPayload,
  ClubPaymentAccountStatus,
  ClubAsaasWithdrawalValidationMode,
  ClubPaymentEnvironment,
  ClubPaymentEvent,
  ClubPaymentEventProcessingStatus,
  ClubPaymentOperationsSummary,
  ClubPaymentValidationResult,
} from '../types';
import { formatCurrency, formatDateTime } from '../utils/formatters';

const EVENT_LIMIT_OPTIONS = [12, 25, 50, 100] as const;

type PaymentFormState = {
  mode: ClubPaymentAccountMode;
  environment: ClubPaymentEnvironment;
  displayName: string;
  apiKey: string;
  webhookToken: string;
  webhookUrl: string;
  pixEnabled: boolean;
  cardEnabled: boolean;
  boletoEnabled: boolean;
  withdrawalEnabled: boolean;
  withdrawalsRequireManualApproval: boolean;
  minDepositReais: string;
  maxDepositReais: string;
  minWithdrawalReais: string;
  maxWithdrawalReais: string;
  isEnabled: boolean;
  withdrawalValidationMode: ClubAsaasWithdrawalValidationMode;
};

const buildDefaultFormState = (clubName?: string): PaymentFormState => ({
  mode: 'EXTERNAL_ACCOUNT',
  environment: 'PRODUCTION',
  displayName: clubName || '',
  apiKey: '',
  webhookToken: '',
  webhookUrl: '',
  pixEnabled: true,
  cardEnabled: false,
  boletoEnabled: false,
  withdrawalEnabled: false,
  withdrawalsRequireManualApproval: true,
  minDepositReais: '20.00',
  maxDepositReais: '',
  minWithdrawalReais: '20.00',
  maxWithdrawalReais: '',
  isEnabled: true,
  withdrawalValidationMode: 'SHADOW',
});

const buildFormStateFromAccount = (account: ClubPaymentAccount | null, clubName?: string): PaymentFormState => {
  if (!account) {
    return buildDefaultFormState(clubName);
  }

  return {
    mode: account.mode,
    environment: account.environment,
    displayName: account.display_name || clubName || '',
    apiKey: '',
    webhookToken: '',
    webhookUrl: account.webhook_url || '',
    pixEnabled: account.pix_enabled,
    cardEnabled: account.card_enabled,
    boletoEnabled: account.boleto_enabled,
    withdrawalEnabled: account.withdrawal_enabled,
    withdrawalsRequireManualApproval: account.withdrawals_require_manual_approval,
    minDepositReais: account.min_deposit_reais.toFixed(2),
    maxDepositReais: account.max_deposit_reais === null ? '' : account.max_deposit_reais.toFixed(2),
    minWithdrawalReais: account.min_withdrawal_reais.toFixed(2),
    maxWithdrawalReais: account.max_withdrawal_reais === null ? '' : account.max_withdrawal_reais.toFixed(2),
    isEnabled: account.status !== 'DISABLED',
    withdrawalValidationMode: account.withdrawal_validation_mode || 'SHADOW',
  };
};

const getPaymentStatusLabel = (status: ClubPaymentAccountStatus | null | undefined): string => {
  switch (status) {
    case 'ACTIVE':
      return 'Ativa';
    case 'PENDING_SETUP':
      return 'Pendente';
    case 'RESTRICTED':
      return 'Restrita';
    case 'DISABLED':
      return 'Desativada';
    default:
      return 'Nao configurada';
  }
};

const getPaymentStatusTone = (status: ClubPaymentAccountStatus | null | undefined): string => {
  switch (status) {
    case 'ACTIVE':
      return 'portal-status-pill--positive';
    case 'PENDING_SETUP':
      return 'portal-status-pill--warning';
    case 'RESTRICTED':
      return 'portal-status-pill--danger';
    case 'DISABLED':
      return 'portal-status-pill--neutral';
    default:
      return 'portal-status-pill--neutral';
  }
};

const getEventStatusTone = (status: ClubPaymentEventProcessingStatus): string => {
  switch (status) {
    case 'PROCESSED':
      return 'portal-status-pill--positive';
    case 'FAILED':
      return 'portal-status-pill--danger';
    case 'PENDING':
      return 'portal-status-pill--warning';
    case 'IGNORED':
    default:
      return 'portal-status-pill--neutral';
  }
};

const getEventStatusLabel = (status: ClubPaymentEventProcessingStatus): string => {
  switch (status) {
    case 'PROCESSED':
      return 'Processado';
    case 'FAILED':
      return 'Falhou';
    case 'PENDING':
      return 'Pendente';
    case 'IGNORED':
      return 'Ignorado';
    default:
      return status;
  }
};

const isExpectedIgnoredEvent = (event: ClubPaymentEvent): boolean => {
  if (event.processing_status !== 'IGNORED') {
    return false;
  }

  const description = String(event.payload_json?.payment?.description || '').trim();
  const externalReference = event.payload_json?.payment?.externalReference;
  const message = String(event.error_message || '').trim();

  return (
    !externalReference &&
    /cobran[cç]a gerada automaticamente a partir de pix recebido/i.test(description) &&
    /pix recebido|cobranca automatica/i.test(message || description)
  );
};

const getEventDisplayStatusLabel = (event: ClubPaymentEvent): string => {
  if (isExpectedIgnoredEvent(event)) {
    return 'Ignorado esperado';
  }

  return getEventStatusLabel(event.processing_status);
};

const getEventDisplayError = (event: ClubPaymentEvent): string => {
  if (isExpectedIgnoredEvent(event)) {
    return 'Evento automatico do Asaas sem vinculo com deposito do portal';
  }

  return event.error_message || 'Sem erro';
};

const SettingsModal: React.FC<{
  title: string;
  eyebrow?: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, eyebrow, subtitle, onClose, children }) => (
  <div className="portal-modal-backdrop" role="dialog" aria-modal="true">
    <div className="portal-modal-card">
      <div className="portal-modal-header">
        <div className="portal-modal-title-group">
          {eyebrow ? <p className="portal-section-card__eyebrow">{eyebrow}</p> : null}
          <h3>{title}</h3>
          {subtitle ? <p className="portal-muted">{subtitle}</p> : null}
        </div>
        <button type="button" className="portal-modal-close" onClick={onClose} aria-label="Fechar modal">
          x
        </button>
      </div>
      <div className="portal-modal-body">{children}</div>
    </div>
  </div>
);

const getModeLabel = (value: ClubPaymentAccountMode): string => {
  return value === 'WHITE_LABEL_SUBACCOUNT' ? 'Subconta white-label' : 'Conta propria do owner';
};

const getEnvironmentLabel = (value: ClubPaymentEnvironment): string => {
  return value === 'SANDBOX' ? 'Sandbox' : 'Producao';
};

const parseOptionalMoney = (value: string): number | null | undefined => {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) {
    return null;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : undefined;
};

const summarizeRegistrationStatus = (snapshot?: Record<string, any>): string => {
  if (!snapshot || typeof snapshot !== 'object') {
    return 'Sem leitura oficial';
  }

  const preferredKeys = [
    'status',
    'general',
    'accountStatus',
    'commercialInfoStatus',
    'bankAccountInfoStatus',
    'documentationStatus',
    'approvalStatus',
  ];

  for (const key of preferredKeys) {
    const candidate = snapshot[key];
    if (candidate !== null && candidate !== undefined && String(candidate).trim()) {
      return `${key}: ${String(candidate)}`;
    }
  }

  const entry = Object.entries(snapshot).find(([, value]) => value !== null && value !== undefined && String(value).trim());
  if (!entry) {
    return 'Snapshot validado';
  }

  return `${entry[0]}: ${String(entry[1])}`;
};

const SettingsPage: React.FC = () => {
  const { selectedClub } = usePortal();
  const [club, setClub] = useState<Club | null>(null);
  const [paymentAccount, setPaymentAccount] = useState<ClubPaymentAccount | null>(null);
  const [summary, setSummary] = useState<ClubPaymentOperationsSummary | null>(null);
  const [events, setEvents] = useState<ClubPaymentEvent[]>([]);
  const [validation, setValidation] = useState<ClubPaymentValidationResult | null>(null);
  const [form, setForm] = useState<PaymentFormState>(buildDefaultFormState());
  const [eventLimit, setEventLimit] = useState<number>(12);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadSettings = useCallback(async () => {
    if (!selectedClub?.id) {
      setClub(null);
      setPaymentAccount(null);
      setSummary(null);
      setEvents([]);
      setValidation(null);
      setForm(buildDefaultFormState());
      setError('');
      setSuccessMessage('');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const [clubPayload, paymentPayload, eventPayload] = await Promise.all([
        clubService.getClub(selectedClub.id),
        clubService.getPaymentAccount(selectedClub.id),
        clubService.getPaymentAccountEvents(selectedClub.id, eventLimit),
      ]);

      setClub(clubPayload);
      setPaymentAccount(paymentPayload.account);
      setSummary(paymentPayload.summary);
      setEvents(eventPayload);
      setValidation(null);
      setForm(buildFormStateFromAccount(paymentPayload.account, clubPayload.name));
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar configuracoes do club');
    } finally {
      setLoading(false);
    }
  }, [eventLimit, selectedClub?.id]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const updateForm = <K extends keyof PaymentFormState>(field: K, value: PaymentFormState[K]) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleValidate = async () => {
    if (!selectedClub?.id) {
      return;
    }

    setValidating(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await clubService.validatePaymentAccount(selectedClub.id, {
        apiKey: form.apiKey.trim() || undefined,
        environment: form.environment,
        webhookUrl: form.webhookUrl.trim() || undefined,
      });

      setValidation(result);
      setSuccessMessage('Credenciais validadas com sucesso no Asaas');
    } catch (nextError: any) {
      setValidation(null);
      setError(nextError.message || 'Erro ao validar credenciais do Asaas');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedClub?.id) {
      return;
    }

    const minDepositReais = parseOptionalMoney(form.minDepositReais);
    const maxDepositReais = parseOptionalMoney(form.maxDepositReais);
    const minWithdrawalReais = parseOptionalMoney(form.minWithdrawalReais);
    const maxWithdrawalReais = parseOptionalMoney(form.maxWithdrawalReais);

    if (
      minDepositReais === undefined ||
      maxDepositReais === undefined ||
      minWithdrawalReais === undefined ||
      maxWithdrawalReais === undefined
    ) {
      setError('Revise os limites financeiros. Use apenas numeros validos.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const account = await clubService.savePaymentAccount(selectedClub.id, {
        mode: form.mode,
        environment: form.environment,
        displayName: form.displayName.trim() || undefined,
        apiKey: form.apiKey.trim() || undefined,
        webhookToken: form.webhookToken.trim() || undefined,
        webhookUrl: form.webhookUrl.trim() || undefined,
        pixEnabled: form.pixEnabled,
        cardEnabled: form.cardEnabled,
        boletoEnabled: form.boletoEnabled,
        withdrawalEnabled: form.withdrawalEnabled,
        withdrawalsRequireManualApproval: form.withdrawalsRequireManualApproval,
        minDepositReais: minDepositReais ?? undefined,
        maxDepositReais,
        minWithdrawalReais: minWithdrawalReais ?? undefined,
        maxWithdrawalReais,
        validateBeforeSave: true,
        isEnabled: form.isEnabled,
        withdrawalValidationMode: form.withdrawalValidationMode,
      } as ClubPaymentAccountPayload);

      setPaymentAccount(account);
      setForm(buildFormStateFromAccount(account, club?.name || selectedClub.name));
      setSuccessMessage('Configuracao financeira salva com sucesso');

      const [paymentPayload, eventPayload] = await Promise.all([
        clubService.getPaymentAccount(selectedClub.id),
        clubService.getPaymentAccountEvents(selectedClub.id, eventLimit),
      ]);

      setSummary(paymentPayload.summary);
      setEvents(eventPayload);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao salvar configuracao financeira');
    } finally {
      setSaving(false);
    }
  };

  const accountStatusLabel = useMemo(
    () => getPaymentStatusLabel(paymentAccount?.status || null),
    [paymentAccount?.status]
  );
  const registrationSummary = useMemo(
    () =>
      summarizeRegistrationStatus(
        validation?.registration_status || paymentAccount?.validation_snapshot?.registration_status
      ),
    [validation?.registration_status, paymentAccount?.validation_snapshot?.registration_status]
  );
  const webhookSummary = useMemo(() => {
    const webhookCheck = validation?.webhook_check || paymentAccount?.validation_snapshot?.webhook_check;
    if (!webhookCheck?.checked) {
      return 'Webhook ainda nao validado';
    }

    return webhookCheck.found_match
      ? 'Webhook encontrado no Asaas'
      : 'Webhook nao localizado na conta validada';
  }, [paymentAccount?.validation_snapshot?.webhook_check, validation?.webhook_check]);

  const selectedEvent = useMemo(
    () => events.find((entry) => entry.id === selectedEventId) || null,
    [events, selectedEventId]
  );

  useEffect(() => {
    if (selectedEventId && !selectedEvent) {
      setSelectedEventId('');
    }
  }, [selectedEvent, selectedEventId]);

  useEffect(() => {
    if (!selectedEvent) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedEventId('');
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedEvent]);

  if (!selectedClub?.id) {
    return (
      <section className="portal-section-card">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Configuracoes</p>
            <h3>Selecione um club</h3>
          </div>
        </div>
        <p className="portal-muted">Escolha um club no topo para configurar dados gerais e a conta financeira externa.</p>
      </section>
    );
  }

  return (
    <div className="portal-page-grid portal-page-stack">
      <section className="portal-section-card portal-section-card--hero portal-section-card--dashboard-hero">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Configuracoes do club</p>
            <h3>Dados institucionais e setup financeiro externo</h3>
          </div>
          <span className={`portal-status-pill ${getPaymentStatusTone(paymentAccount?.status || null)}`}>
            {accountStatusLabel}
          </span>
        </div>
        <p className="portal-muted">
          Esta area prepara a integracao do club com o Asaas. Depositos, saques e webhooks ainda continuam
          em etapas separadas, sem alterar o financeiro operacional atual do portal.
        </p>

        <div className="portal-dashboard-hero-stats">
          <article className="portal-dashboard-hero-stats__item">
            <span>Conta externa</span>
            <strong>{accountStatusLabel}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Ambiente</span>
            <strong>{paymentAccount ? getEnvironmentLabel(paymentAccount.environment) : getEnvironmentLabel(form.environment)}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Webhook</span>
            <strong>{summary?.last_webhook_at ? 'Recebendo' : 'Sem leitura'}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Eventos recentes</span>
            <strong>{events.length}</strong>
          </article>
        </div>
      </section>

      {error ? <div className="portal-banner portal-banner--error">{error}</div> : null}
      {successMessage ? <div className="portal-banner portal-banner--success">{successMessage}</div> : null}
      {loading ? <div className="portal-loading-screen">Carregando configuracoes...</div> : null}

      <section className="portal-metrics-grid portal-dashboard-metrics-grid">
        <MetricCard
          label="Conta externa"
          value={accountStatusLabel}
          helper={paymentAccount ? getEnvironmentLabel(paymentAccount.environment) : 'Sem configuracao salva'}
          tone={paymentAccount?.status === 'ACTIVE' ? 'positive' : 'warning'}
          variant="dashboard"
          theme={paymentAccount?.status === 'ACTIVE' ? 'receivable' : 'warning'}
          badge="Conta"
        />
        <MetricCard
          label="Depositos pendentes"
          value={String(summary?.pending_deposits || 0)}
          helper="Intents aguardando confirmacao"
          variant="dashboard"
          theme="deposit"
          badge="Depositos"
        />
        <MetricCard
          label="Depositos confirmados"
          value={String(summary?.confirmed_deposits || 0)}
          helper="Eventos externos ja conciliados"
          tone="positive"
          variant="dashboard"
          theme="balance"
          badge="Depositos"
        />
        <MetricCard
          label="Saques em analise"
          value={String(summary?.withdrawals_under_review || 0)}
          helper="Inclui solicitado, em analise e reservado"
          variant="dashboard"
          theme="warning"
          badge="Saques"
        />
        <MetricCard
          label="Saques processando"
          value={String(summary?.withdrawals_processing || 0)}
          helper="Transferencias em andamento"
          variant="dashboard"
          theme="fee"
          badge="Saques"
        />
        <MetricCard
          label="Ultimo webhook"
          value={formatDateTime(summary?.last_webhook_at)}
          helper={summary?.last_error_message || 'Sem falha externa recente'}
          tone={summary?.last_error_message ? 'warning' : 'positive'}
          variant="dashboard"
          theme={summary?.last_error_message ? 'warning' : 'rake'}
          badge="Webhook"
        />
      </section>

      <div className="portal-page-grid portal-page-grid--two-columns">
        <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-balance">
          <div className="portal-section-card__header">
            <div>
              <p className="portal-section-card__eyebrow">Dados do club</p>
              <h3>Identidade operacional</h3>
            </div>
          </div>

          {club ? (
            <dl className="portal-detail-grid">
              <div><dt>Nome</dt><dd>{club.name}</dd></div>
              <div><dt>Codigo</dt><dd>{club.code}</dd></div>
              <div><dt>Descricao</dt><dd>{club.description || '-'}</dd></div>
              <div><dt>Publico</dt><dd>{club.is_public ? 'Sim' : 'Nao'}</dd></div>
              <div><dt>Perfil de rake</dt><dd>{club.rake_profile_name || '-'}</dd></div>
              <div><dt>Percentual</dt><dd>{club.rake_percentage}%</dd></div>
              <div><dt>Teto</dt><dd>{formatCurrency(club.rake_cap_reais || 0)}</dd></div>
              <div><dt>Rake acumulado</dt><dd>{formatCurrency(club.total_rake_earned || 0)}</dd></div>
              <div><dt>Criado em</dt><dd>{formatDateTime(club.created_at)}</dd></div>
              <div><dt>Atualizado em</dt><dd>{formatDateTime(club.updated_at)}</dd></div>
            </dl>
          ) : (
            <p className="portal-muted">Selecione um club para ver suas configuracoes.</p>
          )}
        </section>

        <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-deposit">
          <div className="portal-section-card__header">
            <div>
              <p className="portal-section-card__eyebrow">Saude da integracao</p>
              <h3>Leitura atual do Asaas</h3>
            </div>
            <span className="portal-chip">{paymentAccount ? getModeLabel(paymentAccount.mode) : 'Setup inicial'}</span>
          </div>

          <div className="portal-subscription-grid">
            <article className="portal-subscription-item">
              <strong>Status oficial</strong>
              <p className="portal-muted">{registrationSummary}</p>
            </article>
            <article className="portal-subscription-item">
              <strong>Webhook</strong>
              <p className="portal-muted">{webhookSummary}</p>
            </article>
            <article className="portal-subscription-item">
              <strong>Conta identificada</strong>
              <p className="portal-muted">
                {paymentAccount?.display_name || validation?.commercial_info?.name || validation?.commercial_info?.companyName || 'Sem nome validado'}
              </p>
            </article>
          </div>

          <dl className="portal-detail-grid" style={{ marginTop: 18 }}>
            <div><dt>Ambiente</dt><dd>{paymentAccount ? getEnvironmentLabel(paymentAccount.environment) : getEnvironmentLabel(form.environment)}</dd></div>
            <div><dt>API key</dt><dd>{paymentAccount?.has_api_key ? `Final ${paymentAccount.api_key_last4 || '----'}` : 'Nao configurada'}</dd></div>
            <div><dt>Webhook token</dt><dd>{paymentAccount?.has_webhook_token ? 'Configurado' : 'Nao configurado'}</dd></div>
            <div><dt>Validacao de saque</dt><dd>{paymentAccount?.withdrawal_validation_mode || form.withdrawalValidationMode}</dd></div>
            <div><dt>Ultima validacao</dt><dd>{formatDateTime(paymentAccount?.last_validated_at)}</dd></div>
            <div><dt>Conta Asaas</dt><dd>{paymentAccount?.asaas_account_id || '-'}</dd></div>
            <div><dt>Wallet</dt><dd>{paymentAccount?.asaas_wallet_id || '-'}</dd></div>
          </dl>

          {paymentAccount?.last_error_message ? (
            <div className="portal-banner portal-banner--warning" style={{ marginTop: 18 }}>
              Ultima leitura externa: {paymentAccount.last_error_message}
            </div>
          ) : null}
        </section>
      </div>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-manual">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Configuracoes financeiras</p>
            <h3>Conectar e validar a conta Asaas do club</h3>
          </div>
          <div className="portal-section-card__header-actions">
            <button type="button" className="portal-secondary-button" onClick={handleValidate} disabled={validating || saving}>
              {validating ? 'Validando...' : 'Validar credenciais'}
            </button>
            <button type="submit" form="club-payment-account-form" className="portal-primary-button" disabled={saving || validating}>
              {saving ? 'Salvando...' : 'Salvar configuracao'}
            </button>
          </div>
        </div>

        <form id="club-payment-account-form" className="portal-form-grid" onSubmit={handleSubmit}>
          <label>
            <span>Modo da conta</span>
            <select value={form.mode} onChange={(event) => updateForm('mode', event.target.value as ClubPaymentAccountMode)} disabled={saving}>
              <option value="EXTERNAL_ACCOUNT">Conta propria do owner</option>
              <option value="WHITE_LABEL_SUBACCOUNT">Subconta white-label</option>
            </select>
          </label>

          <label>
            <span>Ambiente</span>
            <select
              value={form.environment}
              onChange={(event) => updateForm('environment', event.target.value as ClubPaymentEnvironment)}
              disabled={saving}
            >
              <option value="PRODUCTION">Producao</option>
              <option value="SANDBOX">Sandbox</option>
            </select>
          </label>

          <label>
            <span>Nome de exibicao da conta</span>
            <input
              type="text"
              value={form.displayName}
              onChange={(event) => updateForm('displayName', event.target.value)}
              placeholder="Ex.: Financeiro Club 3BET"
              maxLength={120}
              disabled={saving}
            />
          </label>

          <label>
            <span>Webhook URL</span>
            <input
              type="url"
              value={form.webhookUrl}
              onChange={(event) => updateForm('webhookUrl', event.target.value)}
              placeholder="https://seu-backend/api/webhooks/asaas"
              disabled={saving}
            />
          </label>

          <label>
            <span>Validacao de saque Asaas</span>
            <select
              value={form.withdrawalValidationMode}
              onChange={(event) =>
                updateForm('withdrawalValidationMode', event.target.value as ClubAsaasWithdrawalValidationMode)
              }
              disabled={saving}
            >
              <option value="OFF">OFF (sempre aprova)</option>
              <option value="SHADOW">SHADOW (audita, responde APPROVED)</option>
              <option value="ENFORCED">ENFORCED (aprova ou recusa)</option>
            </select>
          </label>

          <label className="portal-form-grid__full">
            <span>API key do Asaas</span>
            <input
              type="password"
              value={form.apiKey}
              onChange={(event) => updateForm('apiKey', event.target.value)}
              placeholder={paymentAccount?.has_api_key ? 'Ja configurada. Preencha apenas para trocar.' : '$aact_prod_...'}
              autoComplete="new-password"
              disabled={saving}
            />
            <small className="portal-form-helper">
              Deixe em branco para preservar a chave atual. A validacao oficial consulta `status`, `commercialInfo` e `webhooks`.
            </small>
          </label>

          <label className="portal-form-grid__full">
            <span>Webhook token interno</span>
            <input
              type="password"
              value={form.webhookToken}
              onChange={(event) => updateForm('webhookToken', event.target.value)}
              placeholder={paymentAccount?.has_webhook_token ? 'Ja configurado. Preencha apenas para trocar.' : 'Token secreto para validar webhook'}
              autoComplete="new-password"
              disabled={saving}
            />
            <small className="portal-form-helper">
              Esse token sera usado pelo backend para validar webhooks do club. Ele nao substitui a API key.
            </small>
          </label>

          <label>
            <span>Deposito minimo</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={form.minDepositReais}
              onChange={(event) => updateForm('minDepositReais', event.target.value)}
              disabled={saving}
            />
          </label>

          <label>
            <span>Deposito maximo</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={form.maxDepositReais}
              onChange={(event) => updateForm('maxDepositReais', event.target.value)}
              placeholder="Opcional"
              disabled={saving}
            />
          </label>

          <label>
            <span>Saque minimo</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={form.minWithdrawalReais}
              onChange={(event) => updateForm('minWithdrawalReais', event.target.value)}
              disabled={saving}
            />
          </label>

          <label>
            <span>Saque maximo</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={form.maxWithdrawalReais}
              onChange={(event) => updateForm('maxWithdrawalReais', event.target.value)}
              placeholder="Opcional"
              disabled={saving}
            />
          </label>

          <div className="portal-form-grid__full portal-toggle-grid">
            <label className="portal-toggle-card">
              <input type="checkbox" checked={form.isEnabled} onChange={(event) => updateForm('isEnabled', event.target.checked)} disabled={saving} />
              <div>
                <strong>Integracao ativa</strong>
                <span>Desative apenas se o club precisar suspender temporariamente a operacao externa.</span>
              </div>
            </label>

            <label className="portal-toggle-card">
              <input type="checkbox" checked={form.pixEnabled} onChange={(event) => updateForm('pixEnabled', event.target.checked)} disabled={saving} />
              <div>
                <strong>PIX</strong>
                <span>Habilita o PIX como meio futuro de deposito do jogador.</span>
              </div>
            </label>

            <label className="portal-toggle-card">
              <input type="checkbox" checked={form.cardEnabled} onChange={(event) => updateForm('cardEnabled', event.target.checked)} disabled={saving} />
              <div>
                <strong>Cartao</strong>
                <span>Prepara a conta para cobrancas por cartao quando o fluxo web entrar no ar.</span>
              </div>
            </label>

            <label className="portal-toggle-card">
              <input type="checkbox" checked={form.boletoEnabled} onChange={(event) => updateForm('boletoEnabled', event.target.checked)} disabled={saving} />
              <div>
                <strong>Boleto</strong>
                <span>Ativa o boleto apenas se essa experiencia fizer sentido para o club.</span>
              </div>
            </label>

            <label className="portal-toggle-card">
              <input type="checkbox" checked={form.withdrawalEnabled} onChange={(event) => updateForm('withdrawalEnabled', event.target.checked)} disabled={saving} />
              <div>
                <strong>Saque externo</strong>
                <span>Prepara a conta para saques via web, sempre separados do contador interno.</span>
              </div>
            </label>

            <label className="portal-toggle-card">
              <input
                type="checkbox"
                checked={form.withdrawalsRequireManualApproval}
                onChange={(event) => updateForm('withdrawalsRequireManualApproval', event.target.checked)}
                disabled={saving}
              />
              <div>
                <strong>Aprovacao manual de saque</strong>
                <span>Mantenha ativo para clubes que exigem revisao owner/admin antes da transferencia.</span>
              </div>
            </label>
          </div>

          <div className="portal-form-grid__actions portal-form-grid__full portal-card-actions">
            <span className="portal-muted">
              Esta etapa salva apenas setup e validacao da conta. Depositos, saques e webhooks entram nas proximas fases.
            </span>
            <div className="portal-card-actions portal-card-actions--end">
              <button
                type="button"
                className="portal-secondary-button"
                onClick={() => {
                  setForm(buildFormStateFromAccount(paymentAccount, club?.name || selectedClub.name));
                  setValidation(null);
                  setSuccessMessage('');
                  setError('');
                }}
                disabled={saving || validating}
              >
                Restaurar tela
              </button>
              <button type="submit" className="portal-primary-button" disabled={saving || validating}>
                {saving ? 'Salvando...' : 'Salvar configuracao'}
              </button>
            </div>
          </div>
        </form>
      </section>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-ledger">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Eventos recentes</p>
            <h3>Leituras e processamento externo</h3>
            <p className="portal-muted">
              Esta tabela mostra os eventos persistidos no backend para este club. A listagem e limitada aos
              itens mais recentes para manter a leitura objetiva.
            </p>
          </div>
          <div className="portal-section-card__header-actions">
            <label className="portal-club-picker">
              <span>Mostrar</span>
              <select
                value={String(eventLimit)}
                onChange={(event) => setEventLimit(Number(event.target.value))}
                disabled={loading}
              >
                {EVENT_LIMIT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option} recentes
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="portal-secondary-button" onClick={() => void loadSettings()} disabled={loading}>
              Atualizar eventos
            </button>
            <span className="portal-chip">{events.length} evento(s)</span>
          </div>
        </div>

        {events.length ? (
          <div className="portal-table-wrap">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Recebido em</th>
                  <th>Evento</th>
                  <th>Status</th>
                  <th>Referencia</th>
                  <th>Erro</th>
                  <th>Acao</th>
                </tr>
              </thead>
              <tbody>
                {events.map((entry) => (
                  <tr key={entry.id} className={entry.id === selectedEventId ? 'portal-table__row--active' : ''}>
                    <td>{formatDateTime(entry.received_at)}</td>
                    <td>
                      <div className="portal-table__cell-stack">
                        <strong>{entry.event_type}</strong>
                        <span>{entry.provider_event_id || 'Sem id externo'}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`portal-status-pill ${getEventStatusTone(entry.processing_status)}`}>
                        {getEventDisplayStatusLabel(entry)}
                      </span>
                    </td>
                    <td>
                      <div className="portal-table__cell-stack">
                        <span>{entry.provider_payment_id ? `Payment ${entry.provider_payment_id}` : '-'}</span>
                        <span>{entry.provider_transfer_id ? `Transfer ${entry.provider_transfer_id}` : '-'}</span>
                      </div>
                    </td>
                    <td>{getEventDisplayError(entry)}</td>
                    <td>
                      <button
                        type="button"
                        className="portal-secondary-button portal-secondary-button--compact"
                        onClick={() => setSelectedEventId(entry.id)}
                      >
                        {entry.id === selectedEventId ? 'Selecionado' : 'Detalhar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="portal-muted">Nenhum evento externo foi persistido para este club ate o momento.</p>
        )}
      </section>

      {selectedEvent ? (
        <SettingsModal
          eyebrow="Evento externo"
          title={selectedEvent.event_type}
          subtitle={`Recebido em ${formatDateTime(selectedEvent.received_at)}`}
          onClose={() => setSelectedEventId('')}
        >
          <div className="portal-inline-stats">
            <div>
              <dt>Status</dt>
              <dd>{getEventDisplayStatusLabel(selectedEvent)}</dd>
            </div>
            <div>
              <dt>Processado em</dt>
              <dd>{formatDateTime(selectedEvent.processed_at)}</dd>
            </div>
            <div>
              <dt>Provider event</dt>
              <dd>{selectedEvent.provider_event_id || '-'}</dd>
            </div>
            <div>
              <dt>Provider</dt>
              <dd>{selectedEvent.provider}</dd>
            </div>
          </div>

          <div className="portal-form-grid">
            <label>
              <span>Payment id</span>
              <input value={selectedEvent.provider_payment_id || '-'} readOnly />
            </label>
            <label>
              <span>Transfer id</span>
              <input value={selectedEvent.provider_transfer_id || '-'} readOnly />
            </label>
            <label className="portal-form-grid__full">
              <span>Erro registrado</span>
              <input value={getEventDisplayError(selectedEvent)} readOnly />
            </label>
          </div>

          {isExpectedIgnoredEvent(selectedEvent) ? (
            <div className="portal-banner portal-banner--warning">
              Evento automatico do Asaas recebido pela conta do club e descartado com seguranca, sem impacto no
              fluxo local de deposito do portal.
            </div>
          ) : null}

          <div className="portal-settlement-panel">
            <div className="portal-section-card__header">
              <div>
                <p className="portal-section-card__eyebrow">Payload persistido</p>
                <h3>Conteudo tecnico do evento</h3>
              </div>
            </div>
            <pre className="portal-code-block">
              {JSON.stringify(selectedEvent.payload_json || {}, null, 2)}
            </pre>
          </div>
        </SettingsModal>
      ) : null}
    </div>
  );
};

export default SettingsPage;
